/* eslint-disable jsx-a11y/interactive-supports-focus */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { bind } from 'decko';
import { inject } from 'dependable-react';
import { css } from 'emotion';
import { camelCase, omit } from 'lodash';
import { computed, toJS } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import Slider from 'react-slick';

import arrowLeft from '../assets/addOn/left-scroll-icon.svg';
import arrowRight from '../assets/addOn/right-scroll-icon.svg';
import closePersons from '../assets/close-persons.svg';
import closeSvg from '../assets/close.svg';
import dedication from '../assets/dedication.svg';
import { ReactComponent as LogoSvg } from '../assets/logo.svg';
import { BREAKPOINT_MEDIUM, BREAKPOINT_SMALL } from '../consts/breakpoints';
import { APP_DATA_TOKEN, I18N_TOKEN } from '../services/InjectTokens';
import { Avatar } from '../state/models';
import { carouselContainer } from '../styles/carouselContainer';
import { deapify } from '../utils/apify';
import { getPairedImage } from '../utils/getPairedImage';
import { pushGtmData } from '../utils/gtm';
import { Button } from './Button';

const modalOverlay = css`
  position: fixed;
  top: 0;
  left: 0;

  width: 100%;
  height: 100%;

  display: flex;
  justify-content: center;
  align-items: flex-start;

  background-color: #c5bfd630;
  backdrop-filter: blur(6px);

  z-index: 999;
  overflow: auto;
`;

const modalWindow = css`
  position: fixed;
  width: 100%;
  height: 100%;
  background: #fff;
  box-shadow: 12px 13px 45px 0 rgba(153, 57, 142, 0.1);
  padding: 25px 40px;

  overflow: auto;
  -webkit-overflow-scrolling: touch;

  @media (min-width: 840px) {
    position: relative;
    margin: 50px 0;
    border-radius: 15px;
    height: auto;
    max-width: 840px;
    padding: 90px 110px;
  }
`;

const header = css`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  margin-bottom: 50px;

  @media (min-width: ${BREAKPOINT_SMALL}) {
    flex-direction: row;
    justify-content: flex-start;
    align-items: flex-end;
  }

  svg {
    min-width: 130px;
    transform: translateX(15%);

    @media (min-width: ${BREAKPOINT_SMALL}) {
      transform: none;
    }
  }

  h3 {
    margin-top: 40px;
    font-size: 18px;
    color: #414042;
    text-align: center;

    @media (min-width: ${BREAKPOINT_SMALL}) {
      font-size: 24px;
      margin: 0;
      text-align: left;
    }
  }
`;

const body = css`
  margin-bottom: 50px;
`;

const footer = css`
  display: block;
  text-align: center;

  @media (min-width: ${BREAKPOINT_MEDIUM}) {
  }
`;

const card = css`
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;

  height: 100%;

  padding: 20px;
  border-radius: 15px;
  background: #fff;
  box-shadow: 1px 1px 10px 1px rgba(0, 0, 0, 0.08);

  color: #414042;

  @media (min-width: ${BREAKPOINT_SMALL}) {
    width: auto;
    height: auto;
    box-shadow: 1px 1px 25px 1px rgba(0, 0, 0, 0.08);
  }

  &:not(:last-child) {
    margin-bottom: 25px;
  }
`;

const content = css`
  margin-top: 15px;
  font-size: 14px;

  max-height: 200px;
  overflow: auto;

  @media (min-width: ${BREAKPOINT_SMALL}) {
    margin-top: 0;
    margin-left: 45px;
    font-size: 16px;
  }

  strong {
    display: block;
    margin-bottom: 10px;
  }

  p {
    margin: 0;
  }
`;

const icon = css`
  position: relative;
  min-width: 170px;
  min-height: 170px;
  width: 170px;
  height: 170px;

  margin: 0 auto;

  border-radius: 13px;

  display: flex;
  justify-content: center;
  align-items: center;

  img {
    position: absolute;
    width: 115px;
    height: auto;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    @media (min-width: ${BREAKPOINT_SMALL}) {
      width: 85px;
    }
  }

  @media (min-width: ${BREAKPOINT_SMALL}) {
    margin: 0;
    min-width: 100px;
    min-height: 100px;
    width: 100px;
    height: 100px;
  }
`;

const close = css`
  display: block;
  position: absolute;
  top: 24px;
  right: 24px;
  background: url(${closeSvg});
  height: 20px;
  width: 20px;
  background-repeat: no-repeat;
  background-position: center;
  cursor: pointer;
`;

const desktopView = css`
  display: none;
  @media (min-width: ${BREAKPOINT_SMALL}) {
    display: block;
  }
`;

const mobileView = css`
  display: block;

  @media (min-width: ${BREAKPOINT_SMALL}) {
    display: none;
  }

  .slick-slider {
    .slick-list {
      flex: 1;
      padding: 10px 0;
      min-height: 400px;
    }
  }
  .slick-next {
    position: absolute;
    right: 5px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 5;
  }

  .slick-prev {
    position: absolute;
    left: 5px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 5;
  }
  .slick-track {
    display: flex;
    min-height: 400px;
  }

  .slick-track .slick-slide {
    display: flex;
    height: auto;
    align-items: center;
    justify-content: center;
  }

  .slick-slide > div {
    height: 100%;
    width: 220px;
  }
`;

interface IProps {
  isVisible: boolean;
  onClose(): void;
  onConfirm(): void;
}

function Arrow(props: any) {
  const { className, onClick, arrowDirection } = props;
  const arrowImage = arrowDirection === 'left' ? arrowLeft : arrowRight;
  const style = {
    background: `url(${arrowImage})`,
    cursor: 'pointer',
    display: 'block',
    height: '25px',
    width: '25px',
  };

  return <div className={className} style={style} onClick={onClick} role="region" />;
}

const ArrowWrapper = ({ children, ...props }: any) => <div {...props}>{children}</div>;

@observer
export class ConfirmModal extends React.Component<IProps> {
  private i18n = inject(I18N_TOKEN);
  private appState = inject(APP_DATA_TOKEN);

  @computed
  private get elements() {
    const generateDecription = (avatar: Avatar) => {
      const fields = avatar.fields.map(({ uid }) => camelCase(uid));

      const params = {
        ...avatar.defaultPreview,
        ...deapify(avatar.params),
      };

      const tKey =
        Object.keys(avatar.summaries[this.appState.currentLanguage]).find(
          (key) => key === `${camelCase(avatar.uid)}${avatar.genderId === 2 ? 'Male' : 'Female'}Summary`,
        ) || '';

      const paramsWithoutUserFields = omit(
        {
          ...avatar.defaultPreview,
          ...params,
        },
        fields,
      );

      const compiledParams = Object.keys(params).reduce(
        (acc, curr) => ({
          ...acc,
          //@ts-ignore
          [curr]: paramsWithoutUserFields[curr] ? this.i18n.t(paramsWithoutUserFields[curr]) : params[curr],
        }),
        {},
      );

      return this.i18n.t(tKey, {
        ...compiledParams,
      });
    };

    return [
      ...this.appState.avatars.map((avatar) => ({
        description: generateDecription(avatar),
        icon: null,
        images: this.generateAvatar(avatar),
        key: avatar.id,
        title: this.i18n.t(avatar.uid),
      })),
      ...this.appState.moreMagic
        .filter(({ type }) => type === 'dedication_tab' || type === 'child_image')
        .filter(({ isCompleted }) => isCompleted)
        .map((magic: any) => {
          return {
            description: magic.params ? Object.values(magic.params).join(', ') : '',
            icon: magic.type === 'iconed' ? closePersons : dedication,
            images: [],
            key: magic.type,
            title: magic.title,
            path: magic.path,
          };
        }),
    ].filter(Boolean);
  }

  public render() {
    const { isVisible, onClose, onConfirm } = this.props;
    const i18n = inject(I18N_TOKEN);
    if (!isVisible) {
      return null;
    }

    pushGtmData({
      event: 'personalizacija',
      korak: { items: [{ step: `step_confirmation` }] },
    });
    return (
      <div className={modalOverlay}>
        <div className={modalWindow}>
          <div role="button" className={close} onClick={onClose} />
          <div className={header}>
            <LogoSvg />
            <h3>{i18n.t('confirm_title')}</h3>
          </div>
          <div className={body}>
            <div className={desktopView}>
              {this.elements.map((element: any) => (
                <div className={card} key={element.key}>
                  {(element.images.length > 0 || element.icon !== null) && (
                    <div className={icon}>
                      {element.images.length > 0 &&
                        element.images.map((img: any, i: number) => (
                          <img
                            key={`element-${i.toString()}`}
                            src={img.src}
                            style={{ zIndex: img.zIndex, display: img.display }}
                            alt={element.title}
                          />
                        ))}
                      {element.icon !== null && <img src={element.icon} alt={element.title} />}
                    </div>
                  )}
                  <div className={content}>
                    <strong>{element.title}</strong>
                    {Boolean(element.description) && <div dangerouslySetInnerHTML={{ __html: element.description }} />}
                    {Boolean(element.path) && <img className={icon} src={element.path} alt={element.title} />}
                    {Boolean(!element.path) && element.key === 'child_image' && <div>{i18n.t('no_photo')}</div>}
                    {element.description === '' && element.key === 'dedication_tab' && (
                      <div>{i18n.t('no_dedication')}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className={`${carouselContainer} ${mobileView}`}>
              <Slider
                arrows
                infinite={false}
                nextArrow={
                  <ArrowWrapper>
                    <Arrow arrowDirection={'right'} />
                  </ArrowWrapper>
                }
                prevArrow={
                  <ArrowWrapper>
                    <Arrow arrowDirection={'left'} />
                  </ArrowWrapper>
                }
                slidesToScroll={1}
                slidesToShow={1}
                speed={500}
              >
                {this.elements.map((element: any) => (
                  <div className={card} key={element.key}>
                    {(element.images.length > 0 || element.icon !== null) && (
                      <div className={icon}>
                        {element.images.length > 0 &&
                          element.images.map((img: any, i: number) => (
                            <img
                              key={`element-${i.toString()}`}
                              src={img.src}
                              style={{ zIndex: img.zIndex, display: img.display }}
                              alt={element.title}
                            />
                          ))}
                        {element.icon !== null && <img src={element.icon} alt={element.title} />}
                      </div>
                    )}
                    <div className={content}>
                      <strong>{element.title}</strong>
                      {Boolean(element.description) && (
                        <div dangerouslySetInnerHTML={{ __html: element.description }} />
                      )}
                      {Boolean(element.path) && <img className={icon} src={element.path} alt={element.title} />}
                    </div>
                  </div>
                ))}
              </Slider>
            </div>
          </div>
          <div className={footer}>
            <Button value={this.i18n.t('confirm')} onClick={() => onConfirm()} />
          </div>
        </div>
      </div>
    );
  }

  @bind
  private generateZIndex(key: string) {
    const { bookId } = this.appState;

    switch (key) {
      case 'glasses':
        if (bookId === 38 || bookId === 39) {
          return 7;
        }

        return 5;
      case 'eyeColor':
      case 'hair':
        return 6;
      default:
        return 4;
    }
  }

  private generateAvatar(avatar: Avatar) {
    const { genderId, params } = avatar;

    const avatarParams = {
      ...avatar.defaultPreview,
      ...params,
    };

    return Object.keys(avatarParams)
      .map((key) => {
        const { hairColor, hairStyle, beardColor, beardStyle, eyeColor, eyeShape, sex, sexRaceType } = avatarParams;

        const hairImage = getPairedImage(
          avatar,
          { name: 'hairColor', value: hairColor },
          { name: 'hairStyle', value: hairStyle },
        );

        const beardImage = getPairedImage(
          avatar,
          { name: 'beardColor', value: beardColor },
          { name: 'beardStyle', value: beardStyle },
        );

        const eyeImage = getPairedImage(
          avatar,
          { name: 'eyeColor', value: eyeColor },
          { name: 'eyeShape', value: eyeShape },
        );

        const raceImage = getPairedImage(
          avatar,
          { name: 'sex', value: sex },
          { name: 'sexRaceType', value: sexRaceType },
        );

        const otherImages = avatar.previewImages
          .filter(
            (obj: any) =>
              !Object.keys(obj).find(
                (previewKey) => previewKey === 'sexRaceType' || previewKey === 'beard' || previewKey === 'hair',
              ),
          )
          .find((obj: any) => {
            if (obj.genderId === 1) {
              return obj[key] === avatarParams[key] && obj.genderId === 1;
            }

            return obj[key] === avatarParams[key] && obj.genderId === genderId;
          });

        if (key.includes('sexRaceType')) {
          return {
            display: raceImage ? 'block' : 'none',
            src: raceImage && raceImage.image.smallUrl,
            zIndex: this.generateZIndex('race'),
          };
        }

        if (key.includes('hair')) {
          return {
            display: hairImage ? 'block' : 'none',
            src: hairImage && hairImage.image.smallUrl,
            zIndex: this.generateZIndex('hair'),
          };
        }

        if (key.includes('eye') && eyeImage) {
          return {
            display: eyeImage ? 'block' : 'none',
            src: eyeImage && eyeImage.image.smallUrl,
            zIndex: this.generateZIndex('hair'),
          };
        }

        if (key.includes('beard')) {
          return {
            display: beardImage ? 'block' : 'none',
            src: beardImage && beardImage.image.smallUrl,
            zIndex: this.generateZIndex(key),
          };
        }

        return {
          display: otherImages ? 'block' : 'none',
          src: otherImages && otherImages.image.smallUrl,
          zIndex: this.generateZIndex(key),
        };
      })
      .filter(({ src }) => Boolean(src));
  }
}
